import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Row, Accordion } from "react-bootstrap";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import {
  CustomerCompany,
  DerivativeOption,
  FormErrors,
  FormState,
  MarkUpMetrixRecord,
  SelectOption,
  UOMOption,
} from "../../Types/Customermarkupmetrix.types";
import {
  createMarkUpMetrix,
  deleteMarkUpMetrix,
  exportCustomerMarkupMatrix,
  fetchCustomerCompanies,
  fetchDerivatives,
  fetchMarkUpMatrixList,
  fetchUOMs,
  updateMarkUpMetrix,
} from "./CustomermarkupmetrixActions";
import {
  CompactSelect,
  CompactTextField,
} from "../../Components/SharedComponents/SharedComponents";
import { showDeleteConfirmModal } from "../../Components/ConfirmModal";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import { TableColumnVisibilityMenu } from "../../Components/SharedComponents/TableColumnVisibilityMenu";
import { CommonToolbarInternalActions } from "../../Components/SharedComponents/CommonTableToolbarActions";
import { useNavigate } from "react-router-dom";
import HistoryIcon from "@mui/icons-material/History";
import { blockInvalidNumberKeys } from "../../Utils/inputUtils";

// ─── Main Page ────────────────────────────────────────────────────────────────

const CustomerMarkUpMetrix: React.FC = () => {
  const [customerCompanyOptions, setCustomerCompanyOptions] = useState<SelectOption[]>([]);
  const [derivativeOptions, setDerivativeOptions] = useState<SelectOption[]>([]);
  const [uomOptions, setUomOptions] = useState<SelectOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [totalCount, setTotalCount] = useState(0);

  const [tableData, setTableData] = useState<MarkUpMetrixRecord[]>([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const [editId, setEditId] = useState<string | null>(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormState>({
    customer_company: "",
    derivative: "",
    uom: "",
    markup_value: "",
    effective_from: "",
    effective_to: "",
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({
    customer_company: "",
    derivative: "",
    uom: "",
    markup_value: "",
    effective_from: "",
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({
    customer_company: false,
    derivative: false,
    uom: false,
    markup_value: false,
    effective_from: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const selectedOnCurrentPage = tableData.some(
    (row) => rowSelection[row.id]
  );


  const loadDropdowns = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [companies, derivatives, uoms] = await Promise.all([
        fetchCustomerCompanies(),
        fetchDerivatives(),
        fetchUOMs(),
      ]);
      setCustomerCompanyOptions(
        companies.map((c: CustomerCompany) => ({
          value: c.id,
          label: c.customer_company_name,
        }))
      );
      setDerivativeOptions(
        derivatives.map((d: DerivativeOption) => ({
          value: d.id,
          label: d.name,
        }))
      );
      setUomOptions(
        uoms.map((u: UOMOption) => ({ value: u.id, label: u.name }))
      );
    } catch (err) {
      console.error("Error loading dropdown options:", err);
      toast.error("Failed to load dropdown options. Please refresh.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  const loadTableData = useCallback(async (pageIndex: number = 0, pageSize: number = 25) => {
    setLoadingTable(true);
    try {
      const result = await fetchMarkUpMatrixList(pageIndex + 1, pageSize);
      setTableData(result.results);
      setTotalCount(result.count);
    } catch (err) {
      toast.error("Failed to load records.", { position: "top-right", autoClose: 4000 });
    } finally {
      setLoadingTable(false);
    }
  }, []);

  useEffect(() => {
    loadDropdowns();
    loadTableData(0, pagination.pageSize);
  }, [loadDropdowns, loadTableData]);


  const updateFormData = (field: keyof FormState, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (value) setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFocus = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const validateField = (field: string, value: string): string | null => {
    const messages: Record<string, string> = {
      customer_company: "Customer Company is required",
      derivative: "Derivative is required",
      uom: "UOM is required",
      markup_value: "Markup Value is required",
      effective_from: "Effective From is required",
    };
    let error = "";
    if (!value && messages[field]) error = messages[field];
    if (field === "markup_value" && value && isNaN(Number(value)))
      error = "Markup Value must be a valid number";
    setFormErrors((prev) => ({ ...prev, [field]: error }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    return error || null;
  };

  const validateAll = (): boolean => {
    const requiredFields = [
      "customer_company",
      "derivative",
      "uom",
      "markup_value",
      "effective_from",
    ] as const;
    let isValid = true;
    requiredFields.forEach((field) => {
      const value = String(formData[field] ?? "");
      validateField(field, value);
      if (!value) isValid = false;
      if (field === "markup_value" && value && isNaN(Number(value)))
        isValid = false;
    });
    return isValid;
  };

  const handleReset = () => {
    setEditId(null);
    setFormData({
      customer_company: "",
      derivative: "",
      uom: "",
      markup_value: "",
      effective_from: "",
      effective_to: "",
      is_active: true,
    });
    setFormErrors({
      customer_company: "",
      derivative: "",
      uom: "",
      markup_value: "",
      effective_from: "",
    });
    setTouched({
      customer_company: false,
      derivative: false,
      uom: false,
      markup_value: false,
      effective_from: false,
    });
  };

  const hasOverlappingPeriod = (): boolean => {
    if (!formData.effective_from) return false;

    const newStart = new Date(formData.effective_from);
    const newEnd = formData.effective_to ? new Date(formData.effective_to) : null;

    return tableData.some((record) => {
      if (editId && record.id === editId) return false;
      if (
        record.customer_company !== formData.customer_company ||
        record.derivative !== formData.derivative ||
        record.uom !== formData.uom
      ) {
        return false;
      }

      const existingStart = new Date(record.effective_from);
      const existingEnd = record.effective_to
        ? new Date(record.effective_to)
        : null;

      const aEnd = newEnd ?? new Date("9999-12-31");
      const bEnd = existingEnd ?? new Date("9999-12-31");

      return newStart <= bEnd && existingStart <= aEnd;
    });
  };

  const fillFormForEdit = (row: MRT_Row<MarkUpMetrixRecord>) => {
    const r = row.original;
    setEditId(r.id);
    setFormData({
      customer_company: r.customer_company,
      derivative: r.derivative,
      uom: r.uom,
      markup_value: String(r.markup_value),
      effective_from: r.effective_from,
      effective_to: r.effective_to ?? "",
      is_active: r.active,
    });
    setFormErrors({
      customer_company: "",
      derivative: "",
      uom: "",
      markup_value: "",
      effective_from: "",
    });
    setTouched({
      customer_company: false,
      derivative: false,
      uom: false,
      markup_value: false,
      effective_from: false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };


  const handleSubmit = async () => {
    if (!validateAll()) {
      toast.warn("Please fill in all required fields.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (formData.effective_to) {
      const fromDate = new Date(formData.effective_from);
      const toDate = new Date(formData.effective_to);
      if (toDate < fromDate) {
        toast.error("Effective To date cannot be earlier than Effective From.", {
          position: "top-right",
          autoClose: 4000,
        });
        return;
      }
    }

    if (hasOverlappingPeriod()) {
      toast.error(
        "Overlapping period exists for this Customer, Derivative and UOM.",
        {
          position: "top-right",
          autoClose: 4000,
        }
      );
      return;
    }
    setSubmitting(true);
    const payload = {
      customer_company: formData.customer_company,
      derivative: formData.derivative,
      uom: formData.uom,
      markup_value: parseFloat(formData.markup_value),
      effective_from: formData.effective_from,
      effective_to: formData.effective_to || null,
      active: formData.is_active,
    };
    try {
      if (editId) {
        await updateMarkUpMetrix(editId, payload);
        toast.success("Record updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        await createMarkUpMetrix(payload);
        toast.success("Record created successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
      handleReset();
      loadTableData(pagination.pageIndex, pagination.pageSize);
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        `Failed to ${editId ? "update" : "create"} record. Please try again.`;
      toast.error(message, { position: "top-right", autoClose: 4000 });
      console.error("Submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };


  const handleDelete = (row: MRT_Row<MarkUpMetrixRecord>) => {
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this record?",
      content: "This will permanently delete the selected markup record.",
      onOk: async () => {
        try {
          await deleteMarkUpMetrix(row.original.id);
          toast.success("Record deleted successfully!", {
            position: "top-right",
            autoClose: 3000,
          });
          loadTableData(pagination.pageIndex, pagination.pageSize);
        } catch {
          toast.error("Failed to delete record.", {
            position: "top-right",
            autoClose: 4000,
          });
        }
      },
    });
  };


  const handleBulkDelete = () => {
    const selectedIds = Object.keys(rowSelection);
    if (!selectedIds.length) return;

    showDeleteConfirmModal({
      title: `Are you sure you want to delete ${selectedIds.length} selected record(s)?`,
      content: "This action cannot be undone.",
      onOk: async () => {
        try {
          await Promise.all(selectedIds.map((id) => deleteMarkUpMetrix(id)));
          toast.success(
            `${selectedIds.length} record(s) deleted successfully!`,
            {
              position: "top-right",
              autoClose: 3000,
            }
          );
          setRowSelection({});
          loadTableData(pagination.pageIndex, pagination.pageSize);
        } catch {
          toast.error("Failed to delete some records.", {
            position: "top-right",
            autoClose: 4000,
          });
        }
      },
    });
  };

  const handleCopyRow = (row: MRT_Row<MarkUpMetrixRecord>) => {
    const r = row.original;
    setEditId(null);
    setFormData({
      customer_company: r.customer_company,
      derivative: r.derivative,
      uom: r.uom,
      markup_value: String(r.markup_value),
      effective_from: r.effective_from,
      effective_to: r.effective_to ?? "",
      is_active: r.active,
    });
    toast.info("Row copied to form. Modify and click Add to save as new.", {
      position: "top-right",
      autoClose: 4000,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDataDownload = async () => {
    try {
      const blob = await exportCustomerMarkupMatrix();

      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a");

      a.href = url;
      a.download = "customer_markup_matrix.csv";

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };


  const columns = useMemo<MRT_ColumnDef<MarkUpMetrixRecord>[]>(
    () => [
      {
        accessorKey: "customer_company_name",
        header: "Customer Company",
        size: 180,
      },
      {
        accessorKey: "derivative_name",
        header: "Derivative",
        size: 120,
        Cell: ({ cell }) => (
          <span style={{ textTransform: "capitalize" }}>
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "uom_name",
        header: "UOM",
        size: 120,
      },
      {
        accessorKey: "markup_value",
        header: "Markup",
        size: 130,
      },
      {
        accessorKey: "effective_from",
        header: "Effective From",
        size: 140,
      },
      {
        accessorKey: "effective_to",
        header: "Effective To",
        size: 140,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
      },
      {
        accessorKey: "active",
        header: "Active",
        size: 120,
        Cell: ({ cell }) => (
          <span
            style={{
              color: cell.getValue<boolean>() ? "green" : "red",
              fontWeight: "bold",
              fontSize: "0.72rem",
            }}
          >
            {cell.getValue<boolean>() ? "Yes" : "No"}
          </span>
        ),
      },
      {
        accessorKey: "created_user_name",
        header: "Last Updated By",
        size: 130,
      },
      {
        accessorKey: "created_at_readable",
        header: "Last Updated Date",
        size: 130,  
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: "nowrap",  
            overflow: "hidden",
            textOverflow: "ellipsis", 
            maxWidth: "160px",
          },
        },
      },
    ],
    []
  );
  const handleHistory = () => navigate("/settings/log/history/customermarkmatrix");


  const table = useMaterialReactTable({
    columns,
    data: tableData,
    rowCount: totalCount,
    state: { rowSelection, isLoading: loadingTable, pagination },
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater(pagination) : updater;
      setPagination(next);
      loadTableData(next.pageIndex, next.pageSize); 
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100],
    },
    manualPagination: true,
    manualSorting: false,
    manualFiltering: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnFilters: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableFilterMatchHighlighting: true,
    enableDensityToggle: false,
    initialState: {
      showColumnFilters: true,
    },
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,
    layoutMode: "grid",
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row?.id,
    muiTopToolbarProps: () => ({
      sx: { backgroundColor: "#fff", padding: "8px",borderBottom: "1px solid #e5e7eb" },
      className: "top-toolbar-wrapper__new",
    }),
    muiTableProps: {
      sx: {
        tableLayout: "auto",
        width: "100%",
        "& .MuiTableCell-head": {
          whiteSpace: "normal",
          overflow: "visible",
          textOverflow: "unset",
        },
      },
    },
    muiSelectAllCheckboxProps: {
      indeterminate: Object.keys(rowSelection).length > 0 &&
        !tableData.every((row) => rowSelection[row.id]),
      checked: Object.keys(rowSelection).length > 0 &&
        tableData.every((row) => rowSelection[row.id]),
    },
    muiTableContainerProps: () => ({
      sx: { backgroundColor: "#fff" },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: () => ({
      sx: { backgroundColor: "#fff" },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: () => ({
      sx: { backgroundColor: "#fff", padding: "8px" },
      className: "bottom-toolbar-wrapper__new",
    }),
    displayColumnDefOptions: {
      "mrt-row-actions": {
        size: 120,
        minSize: 60,
        maxSize: 120,
        grow: false,
      },
    },
    renderToolbarInternalActions: (props) => <CommonToolbarInternalActions {...props} />,
    renderTopToolbarCustomActions: () => (
      <Box className="table-download__wrapper"
        sx={{
          display: "flex",
          gap: "6px",
          padding: "0px",
          flexWrap: "wrap",
        }}>
        <Button
          onClick={() => handleDataDownload()}
          startIcon={<BrowserUpdatedIcon />}
        >
          Export All Data
        </Button>
        <Button onClick={handleHistory} startIcon={<HistoryIcon />}>History Log</Button>
        {selectedOnCurrentPage && (
          <Button
            onClick={handleBulkDelete}
            style={{ color: "red", fontSize: "small", textTransform: "none" }}
            startIcon={<DeleteIcon style={{ fontSize: "small" }} />}
          >
            Delete Selected ({Object.keys(rowSelection).length})
          </Button>
        )}
      </Box>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: "0.5rem" }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => fillFormForEdit(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy">
          <IconButton size="small" onClick={() => handleCopyRow(row)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  });

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="page-body">
      <ToastContainer />
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Customer Markup Matrix
                  </Accordion.Header>
                  <Accordion.Body>

                    {/* ── Form ── */}
                    <div className="form_contain">
                      <div className="flex formWrapper">
                        <Box
                          sx={{
                            width: "100%",
                            p: { xs: 1, sm: 1 },
                            pt: "20px !important",
                            backgroundColor: "#ffff",
                            borderRadius: "4px",
                            border: editId
                              ? "1px solid #00bcd4"
                              : "1px solid #e0e0e0",
                          }}
                        >
                          <Box
                            className="form_default"
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              justifyContent: "start",
                              gap: 0.5,
                              "& > *": {
                                minWidth: {
                                  xs: "100%",
                                  sm: "48%",
                                  md: "23%",
                                  lg: "15%",
                                  xl: "7.5%",
                                },
                              },
                            }}
                          >
                            {/* Customer Company */}
                            <CompactSelect
                              label="Customer Company"
                              value={formData.customer_company}
                              onChange={(value) =>
                                updateFormData("customer_company", value)
                              }
                              options={customerCompanyOptions}
                              name="customer_company"
                              error={Boolean(
                                formErrors.customer_company &&
                                touched.customer_company
                              )}
                              helperText={
                                touched.customer_company
                                  ? formErrors.customer_company
                                  : ""
                              }
                              validateOnBlur={(value) =>
                                validateField("customer_company", value)
                              }
                              slotProps={{ inputLabel: { shrink: true } }}
                              onFocus={() => handleFocus("customer_company")}
                              disabled={loadingOptions}
                            />

                            {/* Derivative */}
                            <CompactSelect
                              label="Derivative"
                              value={formData.derivative}
                              onChange={(value) =>
                                updateFormData("derivative", value)
                              }
                              options={derivativeOptions}
                              name="derivative"
                              error={Boolean(
                                formErrors.derivative && touched.derivative
                              )}
                              helperText={
                                touched.derivative ? formErrors.derivative : ""
                              }
                              validateOnBlur={(value) =>
                                validateField("derivative", value)
                              }
                              slotProps={{ inputLabel: { shrink: true } }}
                              onFocus={() => handleFocus("derivative")}
                              disabled={loadingOptions}
                            />

                            {/* UOM */}
                            <CompactSelect
                              label="UOM"
                              value={formData.uom}
                              onChange={(value) =>
                                updateFormData("uom", value)
                              }
                              options={uomOptions}
                              name="uom"
                              error={Boolean(formErrors.uom && touched.uom)}
                              helperText={touched.uom ? formErrors.uom : ""}
                              validateOnBlur={(value) =>
                                validateField("uom", value)
                              }
                              slotProps={{ inputLabel: { shrink: true } }}
                              onFocus={() => handleFocus("uom")}
                              disabled={loadingOptions}
                            />

                            {/* Markup Value */}
                            <CompactTextField
                              label="Markup Value"
                              value={formData.markup_value}
                              onChange={(value) =>
                                updateFormData("markup_value", value)
                              }
                              type="number"
                              name="markup_value"
                              error={Boolean(
                                formErrors.markup_value && touched.markup_value
                              )}
                              helperText={
                                touched.markup_value
                                  ? formErrors.markup_value
                                  : ""
                              }
                              validateOnBlur={(value) =>
                                validateField("markup_value", value)
                              }
                              slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: { step: "0.01", min: 0, onKeyDown: blockInvalidNumberKeys },
                              }}
                            />

                            {/* Effective From */}
                            <CompactTextField
                              label="Effective From"
                              value={formData.effective_from}
                              onChange={(value) =>
                                updateFormData("effective_from", value)
                              }
                              type="date"
                              name="effective_from"
                              error={Boolean(
                                formErrors.effective_from &&
                                touched.effective_from
                              )}
                              helperText={
                                touched.effective_from
                                  ? formErrors.effective_from
                                  : ""
                              }
                              validateOnBlur={(value) =>
                                validateField("effective_from", value)
                              }
                              className="date-picker_placeholder"
                              slotProps={{ inputLabel: { shrink: true } }}
                            />

                            {/* Effective To (optional) */}
                            <CompactTextField
                              label="Effective To"
                              value={formData.effective_to}
                              onChange={(value) =>
                                updateFormData("effective_to", value)
                              }
                              type="date"
                              name="effective_to"
                              className="date-picker_placeholder"
                              slotProps={{ inputLabel: { shrink: true } }}
                            />

                            {/* Active + Buttons */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                height: "32px",
                                mb: "0.5rem",
                                flex: {
                                  xs: "1 1 auto",
                                  sm: "1 1 25%",
                                  md: "1 1 25%",
                                  lg: "1 1 25%",
                                  xl: "1 1 12%",
                                },
                                width: {
                                  sm: "25%",
                                  md: "25%",
                                  lg: "25%",
                                  xl: "12%",
                                  xs: "100%",
                                },
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={formData.is_active}
                                    onChange={(e) =>
                                      updateFormData(
                                        "is_active",
                                        e.target.checked
                                      )
                                    }
                                    size="small"
                                    sx={{
                                      color: "#b9baeb",
                                      "&.Mui-checked": { color: "#009db5" },
                                      padding: "4px",
                                    }}
                                  />
                                }
                                label="Active"
                                sx={{
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: "0.75rem",
                                    fontWeight: "bold",
                                    color: "#333",
                                  },
                                  margin: 0,
                                }}
                              />
                              <Button
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={submitting}
                                sx={{
                                  height: { xs: "36px", sm: "32px" },
                                  flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                  minWidth: { xs: "100%", sm: "100px" },
                                  maxWidth: { sm: "150px" },
                                  backgroundColor: "#00bcd4",
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  ml: 1,
                                  "&:hover": { backgroundColor: "#008c9e" },
                                }}
                              >
                                {submitting
                                  ? "Saving..."
                                  : editId
                                    ? "Update"
                                    : "Add"}
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={handleReset}
                                sx={{
                                  height: "32px",
                                  minWidth: "70px",
                                  flexShrink: 0,
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  ml: 1,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Clear
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </div>

                    {/* ── Table ── */}
                    <div
                      className="table-ad__wrapper"
                    >
                      <div className="table-ad__wrapperCard">
                        <MaterialReactTable table={table} />
                      </div>
                    </div>

                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

export default CustomerMarkUpMetrix;